import React, { useEffect, useState } from "react";
import {
    ContentSection,
    Form,
    FormActions,
    FormDatePicker,
    FormTextField,
    Grid,
    GridColumn,
    GridRow,
    TextField,
    useNotifications,
    Checkbox
} from "sprint-common-web";

import { AncillaryComponentRenderer } from "../../../common/components/AncillaryComponentRenderer";
import { MovieComponentRenderer } from "../../../common/components/MovieComponentRenderer";
import { TvSeriesComponentRenderer } from "../../../common/components/TvSeriesComponentRenderer";
import { ContentReferenceType } from "../../../common/types/ContentReferenceType";
import { DeliveryEndpointType } from "../../../common/types/DeliveryEndpointType";
import { HeaderBreadcrumb } from "../../../common/ui/layout/header/header-breadcrumb/HeaderBreadcrumb";
import { PageHeader } from "../../../common/ui/layout/header/PageHeader";
import { ordersRoutes } from "../../OrdersRoutes";
import { CloudAssetsValidationSummary } from "../common/cloud-assets-validation/CloudAssetsValidationSummary";
import { OrderAirlinesSelector } from "./airlines-selector/OrderAirlinesSelector";
import { useCloudAssetsValidation } from "./common/hooks/useCloudAssetsValidation";
import { CreateOrderFormInputs, useCreateOrderForm } from "./common/hooks/useCreateOrderForm";
import { useCreateOrder } from "./common/provider/useCreateOrder";
import { validateDeliveryFolder } from "./common/validation/validateDeliveryFolder";
import { validateTargetFileName } from "./common/validation/validateTargetFileName";
import { OrderContentSelector } from "./content-selector/OrderContentSelector";
import { OrderAncillaryVersionSelector } from "./content-version-selector/ancillary/OrderAncillaryVersionSelector";
import { OrderEpisodeVersionSelector } from "./content-version-selector/episode/OrderEpisodeVersionSelector";
import { OrderMovieVersionSelector } from "./content-version-selector/movie/OrderMovieVersionSelector";
import { OrderEncodeSpecificationSelector } from "./encode-specification-selector/OrderEncodeSpecificationSelector";
import { OrderSoundtracksSelector } from "./soundtracks-selector/OrderSoundtracksSelector";
import { OrderSoundtracksSequencer } from "./soundtracks-sequencer/OrderSoundtracksSequencer";
import { OrderDynamicSubtitlesSelector } from "./subtitles-selector/OrderDynamicSubtitlesSelector";
import { OrderEmbeddedSubtitlesSelector } from "./subtitles-selector/OrderEmbeddedSubtitlesSelector";
import { OrderSubtitlesSequencer } from "./subtitles-sequencer/OrderSubtitlesSequencer";
import { SubtitlesTargetFileNameInput } from "./subtitles-target-file-name-input/SubtitlesTargetFileNameInput";
import { SubtitlesTargetFileNameList } from "./subtitles-target-file-name-list/SubtitlesTargetFileNameList";
import { OrderSystemsSelector } from "./systems-selector/OrderSystemsSelector";
import { OrderTargetResolutionSelector } from "./target-resolution-selector/OrderTargetResolutionSelector";

export const CreateOrderForm: React.FC = () => {
    const { success } = useNotifications();
    const { form, isLoading, goToOrderListing, goToOrderDetails, sourceOrder } = useCreateOrderForm();
    const [preserveFilenames, setPreserveFilenames] = useState(true);

    const {
        cloudAssetsValidationResult,
        isCloudAssetsValidationInProgress,
        hasCloudAssetsValidationError
    } = useCloudAssetsValidation();

    const {
        deliveryType,
        contentType,
        integrator,
        deliveryEndpoint,
        createNewOrder,
        originSubtitlesFileNames,
        setOriginSubtitlesFileNames,
        hasAirlineMismatch,
        contentVersion,
        airline,
        setAirline
    } = useCreateOrder();

    useEffect(() => {
        if (preserveFilenames && sourceOrder) {
            setOriginSubtitlesFileNames(
                sourceOrder.cloudAssets.subtitles.map((subt) => ({
                    languageId: subt.language.id,
                    targetFilename: subt.targetFilename
                }))
            );

            return;
        }

        setOriginSubtitlesFileNames(undefined);
    }, [preserveFilenames, sourceOrder]);

    useEffect(() => {
        if (!contentVersion) {
            return;
        }

        form.setValue("contentVersionId", contentVersion?.version.id, { shouldValidate: true });

        if (contentVersion.type === ContentReferenceType.Ancillary) {
            return;
        }

        if (!contentVersion.version?.brandings?.length) {
            return;
        }

        if (contentVersion.version.brandings.some((item) => item.id === airline?.id)) {
            return;
        }

        setAirline(undefined);
        form.setValue("airlineId", undefined);
    }, [contentVersion]);

    const { handleSubmit, control, errors, formState, clearErrors, trigger } = form;

    const handleCreateOrder = async ({
        dueDate,
        targetFileName,
        subtitlesTargetFileName,
        pONumber,
        deliveryFolder,
        isSubtitlesEmbedded,
        startDate
    }: CreateOrderFormInputs) => {
        const shouldCreateNewOrder = !hasCloudAssetsValidationError && !hasAirlineMismatch;

        if (!shouldCreateNewOrder) {
            return;
        }

        const response = await createNewOrder({
            dueDate: dueDate.toISOString(),
            targetFileName,
            pONumber,
            startDate: startDate.toISOString(),
            deliveryFolder,
            subtitlesTargetFileName,
            isSubtitlesEmbedded,
            originSubtitlesFileNames
        });

        success("Order has been created.");

        goToOrderDetails(response.entityId);
    };

    return (
        <>
            <PageHeader>
                <HeaderBreadcrumb routes={ordersRoutes} />
            </PageHeader>
            <Form onSubmit={handleSubmit(handleCreateOrder)}>
                <Grid>
                    <GridRow style={{ display: "flex" }}>
                        <GridColumn sm={4} style={{ display: "flex" }}>
                            <ContentSection
                                header="Details"
                                isLoading={isLoading}
                                style={{ minHeight: "80vh", flex: 1 }}
                            >
                                <FormTextField
                                    label="PO number"
                                    name="pONumber"
                                    control={control}
                                    errors={errors}
                                    rules={{
                                        required: "Enter PO number"
                                    }}
                                />
                                <OrderAirlinesSelector
                                    label="Airline"
                                    name="airlineId"
                                    control={control}
                                    errors={errors}
                                    previouslySubmitted={formState.submitCount > 0}
                                    trigger={trigger}
                                    clearErrors={clearErrors}
                                />
                                <OrderSystemsSelector
                                    label="System"
                                    name="systemId"
                                    control={control}
                                    errors={errors}
                                    dependsOn={["airlineId"]}
                                />
                                <FormDatePicker
                                    label="Start date"
                                    name="startDate"
                                    rules={{
                                        required: "Select start date"
                                    }}
                                    control={control}
                                    errors={errors}
                                />
                                <OrderSoundtracksSequencer label="Audio tracks order" />
                                <OrderSubtitlesSequencer label="Subtitles order" />
                            </ContentSection>
                        </GridColumn>
                        <GridColumn sm={4} style={{ display: "flex" }}>
                            <ContentSection
                                header="Content"
                                isLoading={isLoading}
                                style={{ minHeight: "80vh", flex: 1 }}
                            >
                                <OrderContentSelector
                                    label="Content"
                                    name="contentId"
                                    control={control}
                                    errors={errors}
                                />
                                {contentType && (
                                    <>
                                        <MovieComponentRenderer subtype={contentType}>
                                            <OrderMovieVersionSelector
                                                control={control}
                                                errors={errors}
                                                dependsOn={["contentId"]}
                                            />
                                        </MovieComponentRenderer>
                                        <TvSeriesComponentRenderer subtype={contentType}>
                                            <OrderEpisodeVersionSelector
                                                control={control}
                                                errors={errors}
                                                dependsOn={["contentId"]}
                                            />
                                        </TvSeriesComponentRenderer>
                                        <AncillaryComponentRenderer subtype={contentType}>
                                            <OrderAncillaryVersionSelector />
                                        </AncillaryComponentRenderer>
                                        <OrderSoundtracksSelector
                                            label="Soundtracks"
                                            name="soundtrackIds"
                                            control={control}
                                            errors={errors}
                                            dependsOn={["contentId"]}
                                            testId="create-order-soundtracks-selector"
                                        />
                                        <OrderEmbeddedSubtitlesSelector
                                            label="Embedded subtitle"
                                            name="embeddedSubtitlesId"
                                            control={control}
                                            errors={errors}
                                            dependsOn={["contentId"]}
                                            testId="create-order-embedded-subtitles-selector"
                                        />
                                        <OrderDynamicSubtitlesSelector
                                            label="Dynamic subtitles"
                                            name="dynamicSubtitlesIds"
                                            control={control}
                                            errors={errors}
                                            dependsOn={["contentId"]}
                                            testId="create-order-dynamic-subtitles-selector"
                                        />
                                        <CloudAssetsValidationSummary
                                            cloudAssetsValidationResult={cloudAssetsValidationResult}
                                            isCloudAssetValidationInProgress={isCloudAssetsValidationInProgress}
                                        />
                                    </>
                                )}
                            </ContentSection>
                        </GridColumn>
                        <GridColumn sm={4} style={{ display: "flex" }}>
                            <ContentSection
                                header="Delivery"
                                isLoading={isLoading}
                                style={{ minHeight: "80vh", flex: 1 }}
                            >
                                <TextField
                                    label="Integrator*"
                                    placeholder="Search and select system to set integrator"
                                    value={integrator?.name}
                                    contentEditable={false}
                                    disabled={true}
                                    data-testId="create-order-integrator-selector"
                                />
                                <TextField
                                    label="Delivery endpoint*"
                                    placeholder="Search and select system to set delivery endpoint"
                                    value={deliveryEndpoint?.name}
                                    contentEditable={false}
                                    disabled={true}
                                    data-testId="create-order-delivery-endpoint-selector"
                                />
                                {deliveryType === DeliveryEndpointType.Aspera && (
                                    <FormTextField
                                        label="Delivery folder"
                                        name="deliveryFolder"
                                        control={control}
                                        errors={errors}
                                        rules={{
                                            required: "Enter delivery folder",
                                            validate: validateDeliveryFolder
                                        }}
                                    />
                                )}
                                <OrderEncodeSpecificationSelector
                                    label="Encode specification"
                                    name="encodeSpecificationId"
                                    control={control}
                                    errors={errors}
                                    dependsOn={["systemId"]}
                                />
                                <OrderTargetResolutionSelector
                                    label="Target resolution"
                                    name="targetResolutionId"
                                    control={control}
                                    errors={errors}
                                />
                                <FormDatePicker
                                    label="Due date"
                                    name="dueDate"
                                    rules={{
                                        required: "Select due date"
                                    }}
                                    control={control}
                                    errors={errors}
                                />

                                <FormTextField
                                    label="Target file name"
                                    name="targetFileName"
                                    control={control}
                                    errors={errors}
                                    rules={{
                                        required: "Enter target file name",
                                        validate: (fileName) => validateTargetFileName(fileName, deliveryType)
                                    }}
                                />
                                <SubtitlesTargetFileNameInput
                                    label="Subtitles target file name"
                                    name="subtitlesTargetFileName"
                                    dependsOn={["systemId"]}
                                    control={control}
                                    errors={errors}
                                />

                                {!!sourceOrder && (
                                    <>
                                        <Checkbox
                                            label="Preserve target filenames from original order"
                                            styles={{ root: { marginTop: 10, marginBottom: 10 } }}
                                            onChange={(_, checked) => setPreserveFilenames(!!checked)}
                                            checked={preserveFilenames}
                                        />

                                        <SubtitlesTargetFileNameList subtitles={sourceOrder.cloudAssets.subtitles} />
                                    </>
                                )}
                            </ContentSection>
                        </GridColumn>
                    </GridRow>
                </Grid>

                <FormActions
                    onCancel={goToOrderListing}
                    submitDisabled={formState.isValidating || isCloudAssetsValidationInProgress}
                    isLoading={formState.isSubmitting || isCloudAssetsValidationInProgress}
                />
            </Form>
        </>
    );
};
